'use client';

import type { ReactNode } from 'react';
import { FileText, CreditCard, Shield, AlertTriangle, Send, CheckCircle, XCircle, LifeBuoy, ShoppingBag, CalendarClock, ScanSearch, CopyCheck } from 'lucide-react';
import { useRouter } from 'next/navigation';
import type { Notification } from '../services/notifications-api';
import { downloadExportReport } from '@/features/invoices/hooks/use-export-report';
import { downloadEfacturaReport } from '@/features/efactura/hooks/use-efactura-export-report';

const PRIORITY_COLORS: Record<string, string> = {
  low: 'border-l-blue-300',
  medium: 'border-l-amber-400',
  high: 'border-l-orange-500',
  critical: 'border-l-red-500',
};

const TYPE_ICONS: Record<string, any> = {
  'invoice.created': FileText,
  'invoice.issued': Send,
  'invoice.overdue': AlertTriangle,
  'efactura.sent': Send,
  'efactura.accepted': CheckCircle,
  'efactura.rejected': XCircle,
  'payment.received': CreditCard,
  'certificate.expiring': Shield,
  'support_new_message': LifeBuoy,
  'marketplace.new_orders': ShoppingBag,
  'export_ready': FileText,
  'export_failed': XCircle,
  'fiscal.deadline': CalendarClock,
  'efactura.review_needed': ScanSearch,
  'efactura.already_registered': CopyCheck,
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return 'acum';
  if (diff < 3600) return Math.floor(diff / 60) + ' min';
  if (diff < 86400) return Math.floor(diff / 3600) + ' ore';
  if (diff < 604800) return Math.floor(diff / 86400) + ' zile';
  return new Date(dateStr).toLocaleDateString('ro-RO');
}

/**
 * Randează textul unei notificări cu:
 *  - emfază bold pe segmentele marcate `**...**` în șabloane (valori-cheie: sumă,
 *    produs, status, cantitate, zile rămase) → <strong>;
 *  - link-uri clicabile pe numărul facturii + numele clientului (din `data`).
 * Backward-compatible: textul fără marcaje `**` se randează exact ca înainte
 * (un singur segment normal), iar link-urile funcționează în continuare.
 */
type RichCtx = {
  invoiceId?: string;
  clientName?: string;
  invoiceLabel: string | null;
  invoicePath: string;
  onNavigate: (href: string) => void;
};

/** Linkifică entitățile cunoscute (factură / client) dintr-un segment de text. */
function renderEntities(text: string, ctx: RichCtx, keyPrefix: string): ReactNode[] {
  const { invoiceId, clientName, invoiceLabel, invoicePath, onNavigate } = ctx;
  const parts: Array<{ text: string; type: 'text' | 'invoice' | 'client' }> = [];
  let remaining = text;

  if (invoiceLabel && remaining.includes(invoiceLabel)) {
    const idx = remaining.indexOf(invoiceLabel);
    if (idx > 0) parts.push({ text: remaining.slice(0, idx), type: 'text' });
    parts.push({ text: invoiceLabel, type: 'invoice' });
    remaining = remaining.slice(idx + invoiceLabel.length);
  }
  if (clientName && remaining.includes(clientName)) {
    const idx = remaining.indexOf(clientName);
    if (idx > 0) parts.push({ text: remaining.slice(0, idx), type: 'text' });
    parts.push({ text: clientName, type: 'client' });
    remaining = remaining.slice(idx + clientName.length);
  }
  if (remaining) parts.push({ text: remaining, type: 'text' });
  if (parts.length === 0) return [text];

  return parts.map((part, i) => {
    const k = `${keyPrefix}-${i}`;
    if (part.type === 'invoice' && invoiceId) {
      return (
        <span
          key={k}
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onNavigate(`${invoicePath}?view=${invoiceId}`); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onNavigate(`${invoicePath}?view=${invoiceId}`); } }}
          className="font-semibold text-primary hover:text-primary-700 dark:hover:text-primary-300 cursor-pointer underline decoration-primary/30 hover:decoration-primary/60 transition-colors"
        >
          {part.text}
        </span>
      );
    }
    if (part.type === 'client' && clientName) {
      return (
        <span
          key={k}
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onNavigate(`/clienti?search=${encodeURIComponent(clientName)}`); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onNavigate(`/clienti?search=${encodeURIComponent(clientName)}`); } }}
          className="font-semibold text-neutral-900 dark:text-neutral-100 hover:text-primary dark:hover:text-primary-300 cursor-pointer underline decoration-neutral-300 dark:decoration-neutral-600 hover:decoration-primary/60 transition-colors"
        >
          {part.text}
        </span>
      );
    }
    return <span key={k}>{part.text}</span>;
  });
}

function RichText({ text, data, onNavigate }: { text: string; data?: Record<string, any>; onNavigate: (href: string) => void }) {
  if (!text) return <>{text}</>;

  const ctx: RichCtx = {
    invoiceId: data?.invoiceId,
    clientName: data?.clientName,
    invoiceLabel: data?.series && data?.number != null ? `${data.series} ${data.number}` : null,
    invoicePath: data?.type === 'proforma' ? '/proforme' : data?.type === 'aviz' ? '/avize' : '/facturi',
    onNavigate,
  };

  // Împarte textul în segmente bold (`**...**`) și normale. Fără marcaje => un
  // singur segment normal (identic cu comportamentul de dinainte).
  const tokens: Array<{ text: string; bold: boolean }> = [];
  const re = /\*\*([\s\S]+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) tokens.push({ text: text.slice(last, m.index), bold: false });
    tokens.push({ text: m[1] ?? '', bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) tokens.push({ text: text.slice(last), bold: false });
  if (tokens.length === 0) tokens.push({ text, bold: false });

  return (
    <>
      {tokens.map((tok, i) =>
        tok.bold ? (
          <strong key={i} className="font-semibold text-neutral-900 dark:text-neutral-100">
            {renderEntities(tok.text, ctx, String(i))}
          </strong>
        ) : (
          <span key={i}>{renderEntities(tok.text, ctx, String(i))}</span>
        ),
      )}
    </>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onRead?: (id: string) => void;
  compact?: boolean;
}

export function NotificationItem({ notification, onRead, compact }: NotificationItemProps) {
  const router = useRouter();
  const Icon = TYPE_ICONS[notification.type] || FileText;
  const priorityBorder = PRIORITY_COLORS[notification.priority] || 'border-l-neutral-300';

  const handleNavigate = (href: string) => {
    if (!notification.isRead) onRead?.(notification.id);
    router.push(href);
  };

  // Daca notificarea are un href in `data` (ex. tichet support: /admin/support/:id),
  // click pe rand navigheaza acolo si marcheaza-o citita. Fallback PE TIP pentru
  // notificarile cu destinatie evidenta create inainte ca `data.href` sa existe
  // (datele notificarii se fixeaza la creare — feedback user pe comanda eMAG).
  const TYPE_FALLBACK_HREF: Record<string, string> = {
    'marketplace.new_orders': '/comenzi-emag',
    'fiscal.deadline': '/contabilitate/calendar',
    'efactura.review_needed': '/efactura?view=review',
    'efactura.already_registered': '/efactura?view=existing',
  };
  const dataHref =
    (notification.data as { href?: string } | undefined)?.href ?? TYPE_FALLBACK_HREF[notification.type];
  const handleRowClick = () => {
    // Raport PDF gata → click descarcă fișierul (auth pe cookie), nu navighează.
    if (notification.type === 'export_ready') {
      const d = notification.data as { token?: string; filename?: string; kind?: string } | undefined;
      if (d?.token) {
        if (!notification.isRead) onRead?.(notification.id);
        // Raportul e-Factura are ruta lui (dreptul e-Factura); celelalte rapoarte se descarcă din cea de facturi.
        const efactura = d.kind === 'efactura_report_pdf';
        (efactura ? downloadEfacturaReport : downloadExportReport)(d.token, d.filename || (efactura ? 'raport-efactura.pdf' : 'raport-facturi.pdf')).catch(() => {});
        return;
      }
    }
    if (dataHref) {
      handleNavigate(dataHref);
      return;
    }
    if (!notification.isRead) onRead?.(notification.id);
  };

  return (
    <div
      onClick={handleRowClick}
      className={`flex w-full items-start gap-3 border-l-3 ${priorityBorder} px-3 ${compact ? 'py-2.5' : 'py-3'} text-left transition-colors cursor-pointer ${
        notification.isRead ? 'bg-white dark:bg-neutral-900' : 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
      }`}
    >
      {/* Unread dot */}
      <div className="mt-1.5 flex-shrink-0">
        {!notification.isRead ? (
          <div className="h-2 w-2 rounded-full bg-primary" />
        ) : (
          <div className="h-2 w-2" />
        )}
      </div>

      {/* Icon */}
      <div className={`mt-0.5 flex-shrink-0 rounded-lg p-1.5 ${
        notification.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
        notification.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' :
        'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
      }`}>
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* În dropdown-ul clopoțelului (compact) textul e mai mic și se înfășoară pe
            mai multe rânduri ca să fie vizibil integral (înainte: line-clamp-1 trunchia
            cu „…"). Pe pagina /notificari (non-compact) rămâne text-sm. */}
        <p className={`${compact ? 'text-xs leading-snug break-words' : 'text-sm'} ${notification.isRead ? 'text-neutral-700 dark:text-neutral-300' : 'text-neutral-900 dark:text-neutral-100 font-medium'}`}>
          <RichText text={notification.title} data={notification.data} onNavigate={handleNavigate} />
        </p>
        {!compact && notification.body && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
            <RichText text={notification.body} data={notification.data} onNavigate={handleNavigate} />
          </p>
        )}
      </div>

      {/* Timestamp */}
      <span className="flex-shrink-0 text-xs text-neutral-400 mt-0.5">
        {timeAgo(notification.createdAt)}
      </span>
    </div>
  );
}
